<template>
    <section class="l2-pricing" id="pricing">
        <div class="l2-pricing__container">
            <div class="l2-pricing__header">
                <h2 class="l2-pricing__title">
                    Simple pricing.<br />
                    <span class="l2-pricing__title-accent">One plan. Everything included.</span>
                </h2>
                <p class="l2-pricing__subtitle">No tiers to guess, nothing to upgrade later. Cancel anytime.</p>
                <p class="l2-pricing__trial mt-3">14-day free trial with full access (no limitations). No credit card required.</p>
            </div>

            <div class="l2-pricing__cards">
                <!-- Early Adopter Card -->
                <div class="l2-pricing__card l2-pricing__card--featured">
                    <div class="l2-pricing__card-glow"></div>
                    <div class="l2-pricing__card-content">
                        <div class="l2-pricing__badge">
                            <v-icon size="14">mdi-star</v-icon>
                            Early Adopter
                        </div>
                        <div class="l2-pricing__card-header">
                            <h3 class="l2-pricing__plan-title">Lock in forever pricing</h3>
                            <p class="l2-pricing__plan-description">
                                Join now and keep this rate for the life of your subscription. Help shape what we build next.
                            </p>
                        </div>

                        <div class="l2-pricing__price-section">
                            <div class="l2-pricing__price-row">
                                <div class="l2-pricing__price">
                                    <span class="l2-pricing__currency"><currency-symbol /></span>
                                    <span class="l2-pricing__amount">10</span>
                                    <span class="l2-pricing__period">/month</span>
                                </div>
                            </div>
                            <div class="l2-pricing__price-divider">
                                <span>or</span>
                            </div>
                            <div class="l2-pricing__price-row">
                                <div class="l2-pricing__price l2-pricing__price--annual">
                                    <span class="l2-pricing__currency"><currency-symbol /></span>
                                    <span class="l2-pricing__amount">100</span>
                                    <span class="l2-pricing__period">/year</span>
                                </div>
                                <span class="l2-pricing__savings">2 months free</span>
                            </div>
                        </div>

                        <HeroEarlyAccessIsland layout="stacked" />
                    </div>
                </div>

                <!-- Standard Card -->
                <div class="l2-pricing__card l2-pricing__card--standard">
                    <div class="l2-pricing__card-content">
                        <div class="l2-pricing__badge l2-pricing__badge--muted">After Launch</div>
                        <div class="l2-pricing__card-header">
                            <h3 class="l2-pricing__plan-title">Standard pricing</h3>
                            <p class="l2-pricing__plan-description">Same great features, same simple plan. Join after launch at regular rates.</p>
                        </div>

                        <div class="l2-pricing__price-section">
                            <div class="l2-pricing__price-row">
                                <div class="l2-pricing__price">
                                    <span class="l2-pricing__currency"><currency-symbol /></span>
                                    <span class="l2-pricing__amount">12</span>
                                    <span class="l2-pricing__period">/month</span>
                                </div>
                            </div>
                            <div class="l2-pricing__price-divider">
                                <span>or</span>
                            </div>
                            <div class="l2-pricing__price-row">
                                <div class="l2-pricing__price l2-pricing__price--annual">
                                    <span class="l2-pricing__currency"><currency-symbol /></span>
                                    <span class="l2-pricing__amount">120</span>
                                    <span class="l2-pricing__period">/year</span>
                                </div>
                                <span class="l2-pricing__savings">2 months free</span>
                            </div>
                        </div>

                        <v-btn block class="l2-btn-secondary" color="primary" size="large" variant="outlined" rounded="pill" disabled>
                            Available at launch
                        </v-btn>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import HeroEarlyAccessIsland from '@/pages/Landing/islands/HeroEarlyAccessIsland.vue';

    defineProps<{
        features: string[];
    }>();
</script>

<style lang="scss" scoped>
    .l2-pricing {
        position: relative;
        padding: var(--l2-section-padding-y) 24px;
        background: var(--l2-bg-elevated);

        &__container {
            max-width: 900px;
            margin: 0 auto;
        }

        &__header {
            text-align: center;
            margin-bottom: 48px;
        }

        &__title {
            font-size: clamp(2rem, 4vw, 3rem);
            font-weight: 700;
            line-height: 1.2;
            color: var(--l2-text);
            margin-bottom: 16px;
        }

        &__title-accent {
            color: var(--landing-muted);
        }

        &__subtitle {
            font-size: 1.0625rem;
            line-height: 1.6;
            color: var(--landing-muted);
            margin: 0;
        }

        &__trial {
            font-size: 0.9375rem;
            line-height: 1.6;
            color: var(--landing-muted);
            margin: 0;
        }

        &__cards {
            display: grid;
            grid-template-columns: 1fr;
            gap: 24px;
            margin-bottom: 40px;

            @media (min-width: 768px) {
                grid-template-columns: repeat(2, 1fr);
                align-items: stretch;
            }
        }

        &__card {
            position: relative;
            border-radius: var(--l2-radius);
            overflow: hidden;
            height: 100%;

            &--featured {
                .l2-pricing__card-content {
                    background: var(--l2-bg);
                    border: 1px solid var(--l2-border);
                }
            }

            &--standard {
                .l2-pricing__card-content {
                    background: var(--l2-card-muted-bg);
                    border: 1px solid var(--l2-border-subtle);
                }

                .l2-pricing__plan-title,
                .l2-pricing__plan-description,
                .l2-pricing__amount,
                .l2-pricing__period,
                .l2-pricing__currency {
                    opacity: 0.7;
                }
            }
        }

        &__card-glow {
            position: absolute;
            inset: -2px;
            background: linear-gradient(135deg, var(--l2-accent) 0%, transparent 30%, transparent 70%, var(--l2-accent-secondary) 100%);
            opacity: 0.4;
            z-index: 0;
            border-radius: inherit;
        }

        &__card-content {
            position: relative;
            z-index: 1;
            padding: 32px;
            border-radius: var(--l2-radius);
            height: 100%;
            display: flex;
            flex-direction: column;

            @media (min-width: 600px) {
                padding: 40px;
            }
        }

        &__badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: var(--l2-bg);
            background: linear-gradient(135deg, var(--l2-accent) 0%, var(--l2-accent-secondary) 100%);
            padding: 6px 14px;
            border-radius: 20px;
            margin-bottom: 20px;
            width: fit-content;

            .v-icon {
                color: var(--l2-bg) !important;
            }

            &--muted {
                background: var(--l2-border-subtle);
                color: var(--l2-text-muted);
            }
        }

        &__card-header {
            margin-bottom: 24px;
        }

        &__plan-title {
            font-size: 1.375rem;
            font-weight: 600;
            color: var(--l2-text);
            margin-bottom: 8px;
        }

        &__plan-description {
            font-size: 0.875rem;
            line-height: 1.6;
            color: var(--landing-muted);
            margin: 0;
        }

        &__price-section {
            margin-bottom: 28px;
            flex-grow: 1;
        }

        &__price-row {
            display: flex;
            align-items: baseline;
            gap: 12px;
            flex-wrap: wrap;
        }

        &__price {
            display: flex;
            align-items: baseline;

            &--annual {
                .l2-pricing__amount {
                    font-size: 2rem;
                }
            }
        }

        &__currency {
            font-size: 1.5rem;
            font-weight: 600;
            color: var(--l2-text);
            margin-right: 2px;
        }

        &__amount {
            font-size: 3rem;
            font-weight: 700;
            color: var(--l2-text);
            line-height: 1;
        }

        &__period {
            font-size: 1rem;
            font-weight: 500;
            color: var(--l2-text-muted);
            margin-left: 4px;
        }

        &__price-note {
            font-size: 0.8125rem;
            font-weight: 500;
            color: var(--l2-accent);
            background: var(--l2-accent-soft);
            padding: 4px 10px;
            border-radius: 12px;
        }

        &__price-divider {
            display: flex;
            align-items: center;
            margin: 16px 0;
            gap: 12px;

            &::before,
            &::after {
                content: '';
                flex: 1;
                height: 1px;
                background: var(--l2-border-subtle);
            }

            span {
                font-size: 0.75rem;
                font-weight: 500;
                color: var(--l2-text-subtle);
                text-transform: uppercase;
                letter-spacing: 0.05em;
            }
        }

        &__savings {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #34d399;
            background: rgba(52, 211, 153, 0.1);
            padding: 4px 10px;
            border-radius: 12px;
        }

        &__features-card {
            background: var(--l2-bg-card);
            border: 1px solid var(--l2-border-subtle);
            border-radius: var(--l2-radius);
            padding: 32px;

            @media (min-width: 600px) {
                padding: 40px;
            }
        }

        &__features-title {
            font-weight: 600;
            font-size: 1rem;
            color: var(--l2-text);
            margin-bottom: 20px;
            text-align: center;
        }

        &__features-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 12px;

            @media (min-width: 500px) {
                grid-template-columns: repeat(2, 1fr);
            }

            @media (min-width: 768px) {
                grid-template-columns: repeat(3, 1fr);
            }
        }

        &__feature {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 0.9375rem;
            color: var(--l2-text-muted);

            .v-icon {
                color: var(--l2-accent);
                flex-shrink: 0;
            }
        }
    }

    .l2-pricing__card-content .l2-btn-primary {
        margin-top: auto;
    }

    .l2-btn-secondary {
        border-color: var(--l2-border-subtle) !important;
        color: var(--l2-text-subtle) !important;
        margin-top: auto;

        &:disabled {
            opacity: 0.6;
        }
    }

    :deep(.l2-pricing__card--featured .early-access--stacked) {
        width: 90%;
        margin-left: auto;
        margin-right: auto;
    }

    @media (max-width: 600px) {
        .l2-pricing {
            &__header {
                margin-bottom: 32px;
            }

            &__cards {
                margin-bottom: 28px;
            }
        }
    }
</style>
